<!--
  - Copyright (C) 2019. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div class="clients">
    <!-- BEGIN: sidebar client creation -->
    <sidebar class="sidebar" ref="sidebar"
             title="Client Details"
             subtitle="Enter control details of your client"
             v-if="isDriverDataComplete">
      <template v-slot:sidebar-content>
        <div  class="text-capitalize">
          <b-form @submit="addClient" id="client-add-form">
            <b-form-group label="Type:*" label-for="inputClientType">
              <b-form-select id="inputClientType"
                             v-model="selectedDriverAliasId"
                             v-on:change="onSelectedDriverChanged"
                             :options="driverSelectList"
                             required>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Name:*" label-for="inputname">
              <b-form-input id="inputname" placeholder="Enter a name"
                            v-model="clientName" required>
              </b-form-input>
            </b-form-group>
            <b-form-group v-for="i of inputValues" :key="i.name"
                          :label="i.name+':*'"
                          :label-for="'input'+i.name">
              <b-form-select
                v-if="selectedDriver.getFactoryValues(i.name)"
                :value="null" v-model="i.value"
                :options="selectedDriver.getFactoryValues(i.name)"
                required>
                <template v-slot:first>
                  <b-form-select-option :value="null" disabled>
                    Please select an option
                  </b-form-select-option>
                </template>
              </b-form-select>
              <b-form-input
                v-else
                :id="'input'+i.name" :placeholder="'Enter client '+i.name"
                v-model="i.value"
                required
              ></b-form-input>
            </b-form-group>
            <b-button type="submit" variant="success" class="mt-5"
                      id="submit-client-button" block>
              Save client
            </b-button>
          </b-form>
        </div>
      </template>
    </sidebar>
    <!-- END: sidebar client creation -->
    <!-- Secondary navigation -->
    <navbar-secondary>
      <template v-slot:items>
        <b-nav-item>
          <b-button
            class="show-sidebar-button"
            variant="primary"
            v-on:click="openSideBar()">
            <b-icon-plus></b-icon-plus> New Client
          </b-button>
        </b-nav-item>
      </template>
    </navbar-secondary>
    <!-- BEGIN: content -->
    <b-container class="py-4" fluid>
      <!-- Errors -->
      <b-alert variant="danger" show v-if="driverError">-->
        <b>Driver Type error:</b> {{ driverError }}
      </b-alert>
      <b-alert variant="danger" show v-if="clientError">
        <b>Driver error:</b> {{ clientError }}
      </b-alert>

      <div class="search-wrapper mb-5">
        <input type="text" v-model="searchQuery" ref="search"
               placeholder="Filter by name or type"/>
      </div>

      <!-- Table content -->
      <b-table striped hover :items="resultQuery" v-if="isDriverDataComplete"
               id="data-table" :per-page="perPage" :fields="tablesFields"
               :current-page="currentPage">
        <template v-slot:cell(actions)="row">
          <b-button variant="danger" size="sm"
                    v-on:click="removeClient(row.value.id)">
            <b-icon-trash></b-icon-trash>
          </b-button>
        </template>
      </b-table>

      <div v-if="!resultQuery.length && !loading" class="text-center">
        Nothing found with this filter...
      </div>

      <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"
                    aria-controls="data-table" align="center" class="mt-5">
      </b-pagination>
    </b-container>
    <b-alert
      v-model="loading"
      class="position-fixed fixed-bottom m-0 rounded-0 text-center"
      style="z-index: 2000;"
      variant="primary">
      <b-icon-alarm></b-icon-alarm> Loading, please wait...
    </b-alert>
    <!-- END: content -->
  </div>
</template>

<script>
  export default {
    components: {
      NavbarSecondary: () => import('@/components/NavbarSecondary.vue'),
      Sidebar: () => import('@/components/Sidebar.vue')
    },

    data () {
      return {
        loading: false,
        clientError: null,
        driverError: null,
        driverSelectList: null,
        driverData: [],
        selectedDriverAliasId: null,
        selectedDriverName: null,
        clients: [],
        isSidePanel: false,
        inputValues: [],
        clientName: '',
        isDriverDataComplete: false,
        searchQuery: null,
        perPage: 20,
        currentPage: 1,
        tablesFields: [
          {
            key: 'name',
            sortable: true
          },
          {
            key: 'info',
            sortable: true,
            thClass: 'd-none d-md-table-cell',
            tdClass: 'd-none d-md-table-cell'
          },
          {
            key: 'type',
            sortable: true
          },
          {
            key: 'actions',
            label: '',
            thClass: 'text-right',
            tdClass: 'text-right'
          }
        ],
        driverAliases: {}
      }
    },
    watch: {
      isDriverDataComplete () {
        this.$refs.search.focus()
      }
    },
    computed: {
      selectedDriver () {
        return this.getDriverByAliasId(this.selectedDriverAliasId)
      },
      tableData () {
        let tableData = []
        this.clients.map(c => {
          tableData.push({
            name: c.clientName,
            info: this.getDriverById(c.driverId).info,
            type: this.getDriverAlias(c),
            actions: { id: c.clientId }
          })
        })
        return tableData
      },
      resultQuery () {
        if (this.searchQuery) {
          return this.tableData.filter((item) => {
            return this.searchQuery
              .toLowerCase().split(' ')
              .every(v => item.name.toLowerCase().includes(v) ||
                item.type.toLowerCase().includes(v))
          })
        } else {
          return this.tableData
        }
      },
      rows () {
        return this.resultQuery.length
      }
    },

    methods: {
      updateDrivers (nextToken = null) {
        if (nextToken == null) {
          this.isDriverDataComplete = false
          this.driverData = []
          this.driverError = null
        }
        return this.$driverManager.listDrivers(nextToken)
          .then((data) => {
            let drivers = []
            let drivers_ = []
            for (let d of data.driversList) {
              drivers_.push(d)
            }
            this.driverData = Array.prototype.concat(this.driverData, drivers_)
            if (data.nextPageToken) {
              return this.updateDrivers(data.nextPageToken)
            } else {
              for (let driver of this.driverData) {
                drivers.push(
                  { value: driver.alias + driver.driverId, text: driver.alias }
                )
              }
              this.driverSelectList = drivers
              this.selectedDriverAliasId = this.driverSelectList[0].value
              this.selectedDriverName = this.driverSelectList[0].text
              this.isDriverDataComplete = true
              this.onSelectedDriverChanged()
            }
          })
          .catch((err) => {
            console.error(err)
            this.driverError = err
          })
      },

      updateClients (nextToken = null) {
        if (nextToken == null) {
          this.clients = []
          this.driverAliases = {}
          this.clientError = null
        }
        return this.$driverManager.listClients(nextToken)
          .then(async data => {
            let clients = []
            for (let d of data.clientsList) {
              let driver = this.getDriverById(d.driverId)
              let alias = await this.$driverManager.computeDriverAlias(d, driver)
              if (alias) {
                this.driverAliases[d.clientId] = alias
              }
              clients.push(d)
            }
            this.clients = Array.prototype.concat(this.clients, clients)
            if (data.nextPageToken) {
              return this.updateClients(data.nextPageToken)
            }
          })
          .catch((err) => {
            console.error(err)
            this.clientError = err
          })
      },

      updateData () {
        this.loading = true
        return this.updateDrivers()
          .then(() => {
            this.updateClients().then(() => {
              this.loading = false
            })
          })
      },

      openSideBar () {
        this.onSelectedDriverChanged()
        this.$refs.sidebar.open()
      },

      onSelectedDriverChanged () {
        this.clientName = ''
        this.inputValues = []
        let fixedParameter = this.selectedDriver.fixedFactoryParameter
        for (let key in this.selectedDriver.factoryParameter) {
          if (fixedParameter && (key in fixedParameter)) {
            continue
          }
          this.inputValues.push({ name: key, value: '' })
        }
        this.$forceUpdate()
      },

      removeClient (clientId) {
        return this.$driverManager.deleteClient(clientId)
          .then(() => {
            this.updateClients()
          })
          .catch((err) => {
            console.error(err)
            this.clientError = err
          })
      },

      getDriverById (driverId) {
        return this.driverData.find(d => d.driverId === driverId)
      },

      getDriverByAliasId (aliasId) {
        return this.driverData.find(
          d => aliasId.includes(d.driverId) && aliasId.includes(d.alias)
        )
      },

      getDriverAlias (client) {
        if (client.clientId in this.driverAliases) {
          return this.driverAliases[client.clientId]
        }
        return this.getDriverById(client.driverId).name
      },

      addClient (evt = null) {
        if (evt) {
          evt.preventDefault()
        }
        let values = {}
        for (let i of this.inputValues) {
          values[i.name] = i.value
        }
        let fixedParameter = this.selectedDriver.fixedFactoryParameter
        if (fixedParameter) {
          values = Object.assign(values, fixedParameter)
        }

        this.$refs.sidebar.close()
        return this.$driverManager.createClient(
          this.clientName, this.selectedDriver, values)
          .then(() => {
            this.updateClients()
          })
          .catch((error) => {
            console.error(error)
            this.clientError = error
          })
      }
    },

    created () {
      this.updateData()
    }
  }
</script>

<style scoped>

</style>
